import React, { useEffect, useMemo, useState } from "react";
import "./vital-sign-form.scss";
import { Form, Formik } from "formik";
import { visitUpdate } from "../../../../service/api/visit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleLeft, faXmark } from "@fortawesome/free-solid-svg-icons";

const moment = require("moment");

const VitalSignForm = ({ setShow, visitDetails, updateState, previousVitals, setVisitDetails }) => {
    const [height, setHeight] = useState(0)
    const [heightMesure, setHeightMesure] = useState("1")
    const [info, setInfo] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const setInfoFun = (key, value) => {
        let temp = { ...info };
        setInfo({ ...temp, [key]: value });
    };

    function getPayload(payload = {}) {
        payload["vitals.height"] = ((+info?.height_ft || 0) + "." + ((+info?.height_inc || 0))) || "";
        payload["vitals.weight"] = ((+info?.weight_kg || 0) + ((+info?.weight_gram || 0) / 1000))?.toFixed(2)?.toString() || "";
        payload["vitals.bmi"] = info?.bmi || "";
        payload["vitals.bp"] = (info?.bp_upper || "") + "/" + (info?.bp_lower || "");
        payload["vitals.hr"] = info?.hr || "";
        payload["vitals.temperature"] = info?.temperature || "";
        payload["vitals.spo2"] = info?.spo2 || "";
        payload["vitals.rr"] = info?.rr || "";
        payload["vitals.note"] = info?.note || "";
        payload["vitals.date"] = moment().format("YYYY-MM-DD");
        return payload;
    }

    const onSubmit = async (values, { resetForm }) => {
        saveFun(getPayload(values));
        setShow(false);
    };

    const handleKeyDown = (event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 's') {
            event.preventDefault();
            saveFun(getPayload());
        }
    };

    const saveFun = async (values) => {
        setIsSubmitting(true);
        await visitUpdate(values, visitDetails._id)
            .then((res) => {
                if (res?.status == 200) {
                    setIsSubmitting(false);
                    setVisitDetails(res?.data);
                    updateState();
                    setShow(false);
                }
            })
            .catch((error) => {
                setIsSubmitting(false);
                console.log(error);
            });
    };

    const setPreviousInfoFun = () => {
        let bp = previousVitals.vitals?.bp?.split("/");
        let height = previousVitals.vitals?.height?.split(".");
        if (previousVitals.vitals?.height) {
            const totalInches = (previousVitals.vitals?.height * 12);
            const cm = Math.floor(totalInches * 2.54);
            setHeight(cm);
        }
        setInfo({
            weight_kg: parseInt(previousVitals.vitals?.weight) || "",
            weight_gram: previousVitals.vitals?.weight * 1000 - parseInt(previousVitals.vitals?.weight) * 1000 || "",
            height_ft: height?.length > 0 ? height[0] : "",
            height_inc: height?.length > 1 ? height[1] : "",
            bp_upper: bp?.length > 0 ? bp[0] : "",
            bp_lower: bp?.length > 1 ? bp[1] : "",
            hr: previousVitals.vitals?.hr || "",
            bmi: previousVitals.vitals?.bmi || "",
            temperature: previousVitals.vitals?.temperature || "",
            spo2: previousVitals.vitals?.spo2 || "",
            rr: previousVitals.vitals?.rr || "",
            note: previousVitals.vitals?.note || "",
        });
    };

    useMemo(() => {
        // Guard on the NUMERIC values: the inputs hold strings, and "0" is truthy,
        // so a height of 0 ft 0 in used to slip through and divide to Infinity.
        // Zero height or zero weight -> no BMI, leave it blank.
        const h = (+info?.height_ft || 0) * 0.3048 + (+info?.height_inc || 0) * 0.0254;
        const w = (+info?.weight_kg || 0) + (+info?.weight_gram || 0) / 1000;
        if (h > 0 && w > 0) {
            const bmi = w / Math.pow(h, 2);
            setInfoFun("bmi", bmi.toFixed(2));
        } else setInfoFun("bmi", "");
    }, [info.height_ft, info.height_inc, info.weight_gram, info.weight_kg]);

    useMemo(() => {
        if (height && heightMesure == 2) {
            const totalInches = height / 2.54;
            const feet = parseInt(totalInches / 12);
            const inches = Math.floor(totalInches % 12);
            let temp = { ...info };
            setInfo({ ...temp, height_ft: feet, height_inc: inches });
        }
    }, [height, heightMesure]);

    useEffect(() => {
        if (info) document.addEventListener('keydown', handleKeyDown);
        return () => { document.removeEventListener('keydown', handleKeyDown); };
    }, [info]);

    return (
        <div className="vsm">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="vsm__header">
                <div>
                    <p className="vsm__title">Add Vital Signs</p>
                    <p className="vsm__subtitle">Capture current measurements and update visit vitals.</p>
                </div>
                <button type="button" className="vsm__close" onClick={() => setShow(false)} aria-label="Close">
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            {/* ── Body ────────────────────────────────────────────────────── */}
            <div className="vsm__body">

                {/* Form panel */}
                <div className="vsm__form-panel">
                    <Formik initialValues={{}} onSubmit={onSubmit}>
                        {() => (
                            <Form encType="multipart/form-data" noValidate>

                                <div className="vsf-grid">

                                    {/* Height */}
                                    <div className="vsf-field">
                                        <div className="vsf-field__label-row">
                                            <span className="vsf-field__label">Height</span>
                                            <div className="vsf-toggle">
                                                <button type="button" className={`vsf-toggle__btn${heightMesure == "1" ? " vsf-toggle__btn--active" : ""}`} onClick={() => setHeightMesure("1")}>ft</button>
                                                <button type="button" className={`vsf-toggle__btn${heightMesure == "2" ? " vsf-toggle__btn--active" : ""}`} onClick={() => setHeightMesure("2")}>cm</button>
                                            </div>
                                        </div>
                                        {heightMesure == "1" ? (
                                            <div className="vsf-field__input-row">
                                                <input className="vsf-field__input" type="number" placeholder="0" value={info?.height_ft || ""} onChange={(e) => setInfoFun("height_ft", e.target.value)} />
                                                <span className="vsf-field__unit">ft</span>
                                                <input className="vsf-field__input" type="number" placeholder="0" value={info?.height_inc || ""} onChange={(e) => setInfoFun("height_inc", e.target.value)} />
                                                <span className="vsf-field__unit">in</span>
                                            </div>
                                        ) : (
                                            <div className="vsf-field__input-row">
                                                <input className="vsf-field__input vsf-field__input--wide" type="number" placeholder="0" value={height || ""} onChange={(e) => setHeight(e.target.value)} />
                                                <span className="vsf-field__unit">cm</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Weight */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">Weight</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input" type="number" placeholder="0" value={info?.weight_kg || ""} onChange={(e) => setInfoFun("weight_kg", e.target.value)} />
                                            <span className="vsf-field__unit">kg</span>
                                            <input className="vsf-field__input" type="number" placeholder="0" value={info?.weight_gram || ""} onChange={(e) => setInfoFun("weight_gram", e.target.value)} />
                                            <span className="vsf-field__unit">g</span>
                                        </div>
                                    </div>

                                    {/* BMI */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">BMI <span className="vsf-field__auto-badge">auto</span></span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input vsf-field__input--wide vsf-field__input--readonly" type="text" placeholder="—" value={info.bmi || ""} readOnly />
                                            <span className="vsf-field__unit">kg/m²</span>
                                        </div>
                                    </div>

                                    {/* Temperature */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">Temperature</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input vsf-field__input--wide" type="number" placeholder="98.6" value={info.temperature || ""} onChange={(e) => setInfoFun("temperature", e.target.value)} />
                                            <span className="vsf-field__unit">°F</span>
                                        </div>
                                    </div>

                                    {/* Blood Pressure */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">Blood Pressure</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input" type="number" placeholder="120" value={info?.bp_upper || ""} onChange={(e) => setInfoFun("bp_upper", e.target.value)} />
                                            <span className="vsf-field__sep">/</span>
                                            <input className="vsf-field__input" type="number" placeholder="80" value={info?.bp_lower || ""} onChange={(e) => setInfoFun("bp_lower", e.target.value)} />
                                            <span className="vsf-field__unit">mmHg</span>
                                        </div>
                                    </div>

                                    {/* Heart Rate */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">Heart Rate</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input vsf-field__input--wide" type="number" placeholder="72" value={info.hr || ""} onChange={(e) => setInfoFun("hr", e.target.value)} />
                                            <span className="vsf-field__unit">/min</span>
                                        </div>
                                    </div>

                                    {/* SPO2 */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">SpO₂</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input vsf-field__input--wide" type="number" placeholder="98" value={info.spo2 || ""} onChange={(e) => setInfoFun("spo2", e.target.value)} />
                                            <span className="vsf-field__unit">%</span>
                                        </div>
                                    </div>

                                    {/* Respiratory Rate */}
                                    <div className="vsf-field">
                                        <span className="vsf-field__label">Respiratory Rate</span>
                                        <div className="vsf-field__input-row">
                                            <input className="vsf-field__input vsf-field__input--wide" type="number" placeholder="16" value={info.rr || ""} onChange={(e) => setInfoFun("rr", e.target.value)} />
                                            <span className="vsf-field__unit">/min</span>
                                        </div>
                                    </div>

                                    {/* Note — full width */}
                                    <div className="vsf-field vsf-field--full">
                                        <span className="vsf-field__label">Additional Note</span>
                                        <textarea
                                            className="vsf-field__textarea"
                                            rows={3}
                                            placeholder="Optional notes…"
                                            value={info.note || ""}
                                            onChange={(e) => setInfoFun("note", e.target.value)}
                                        />
                                    </div>

                                </div>

                                <div className="vsf-footer">
                                    <button type="submit" className="vsf-save-btn" disabled={isSubmitting}>
                                        {isSubmitting
                                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</>
                                            : "Save & Close"}
                                    </button>
                                </div>

                            </Form>
                        )}
                    </Formik>
                </div>

                {/* Previous vitals panel */}
                <div className="vsm__prev-panel">
                    <div className="vsm__prev-head">
                        <span className="vsm__prev-title">Previous Vitals</span>
                        {previousVitals && (
                            <button type="button" className="vsm__use-btn" onClick={setPreviousInfoFun}>
                                <FontAwesomeIcon icon={faAngleDoubleLeft} className="me-1" />
                                Use
                            </button>
                        )}
                    </div>

                    {previousVitals ? (
                        <div className="vsm__prev-list">
                            {[
                                { label: "Date",        value: previousVitals.vitals?.createdAt?.slice(0, 10), unit: "" },
                                { label: "Weight",      value: previousVitals.vitals?.weight,     unit: "kg" },
                                { label: "Height",      value: previousVitals.vitals?.height,     unit: "ft" },
                                { label: "BMI",         value: previousVitals.vitals?.bmi,        unit: "kg/m²" },
                                { label: "Temperature", value: previousVitals.vitals?.temperature, unit: "°F" },
                                { label: "BP",          value: previousVitals.vitals?.bp,         unit: "mmHg" },
                                { label: "Heart Rate",  value: previousVitals.vitals?.hr,         unit: "/min" },
                                { label: "SpO₂",        value: previousVitals.vitals?.spo2,       unit: "%" },
                                { label: "Resp. Rate",  value: previousVitals.vitals?.rr,         unit: "/min" },
                            ].map(({ label, value, unit }) => (
                                <div key={label} className="vsm__prev-row">
                                    <span className="vsm__prev-label">{label}</span>
                                    <span className="vsm__prev-value">
                                        {value || "—"}{value && unit ? <span className="vsm__prev-unit"> {unit}</span> : null}
                                    </span>
                                </div>
                            ))}
                            {previousVitals.vitals?.note && (
                                <div className="vsm__prev-note">
                                    <span className="vsm__prev-label">Note</span>
                                    <span className="vsm__prev-value">{previousVitals.vitals.note}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="vsm__prev-empty">No previous vitals recorded.</p>
                    )}
                </div>

            </div>
        </div>
    );
};

export default VitalSignForm;
